import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GestureResponderEvent, Pressable, Text } from 'react-native';
import { calendarStyles } from './calendar.styles';
import { useCalendarDrag } from './calendar.drag';
import type { CalendarEvent } from './calendar.types';

export interface EventPillProps {
  /** The event to render (color/title/asset). */
  event: CalendarEvent;
  /** Highlight as the currently-open activity (inset white ring). */
  selected?: boolean;
  /** Tighter vertical padding for the dense week grid (§1). */
  dense?: boolean;
  /** Show the linked asset as a second line (§1 `showAssetInPill`). */
  showAsset?: boolean;
  /** Press handler — opens the activity (consumer wires `/activity/:id`). */
  onPress?: (id: string) => void;
  /** Test ID for end-to-end selectors. */
  testID?: string;
}

/**
 * A single activity pill placed in a calendar cell (plan 0017 §1 "Event pill").
 * radius 6, padH 6 / padV 3 (1 dense), full-width left-aligned, background = the
 * event color, white 11/15 single-line title, optional 10/13 white@75% asset line.
 *
 * Drag (§3, P3): when the calendar is `draggable` (a CalendarDragContext is
 * mounted + enabled), a LONG-PRESS picks the pill up and a TAP still opens it.
 * While dragging, window-level pointer listeners (web-only) drive the controller's
 * drop-target highlight and fire the drop on release. The origin pill dims while
 * it is the one being dragged.
 *
 * Hover brightness (§6.2): RN-Web has no CSS `filter` hover, so we approximate
 * gripp-OS's `hover:brightness-105` with a subtle opacity lift on hover.
 */
export const EventPill: React.FC<EventPillProps> = ({
  event,
  selected = false,
  dense = false,
  showAsset = false,
  onPress,
  testID,
}) => {
  const [hovered, setHovered] = useState(false);
  const drag = useCalendarDrag();
  const draggable = !!drag?.enabled;
  const isDragging = drag?.draggingId === event.id;

  // Last pointer position seen on this pill, in CLIENT (viewport) coords to match
  // getBoundingClientRect-based hit-testing. onLongPress carries no coordinates,
  // so we capture them from onPressIn to seed the drag. RN-Web's nativeEvent
  // exposes clientX/clientY; fall back to page coords if a platform omits them.
  const lastPointRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // True once a long-press has promoted this gesture to a drag — suppresses the
  // trailing onPress so a drag never also "opens" the activity.
  const draggedRef = useRef(false);

  const recordPoint = useCallback((e: GestureResponderEvent) => {
    const ne = e.nativeEvent as typeof e.nativeEvent & { clientX?: number; clientY?: number };
    lastPointRef.current = { x: ne.clientX ?? ne.pageX, y: ne.clientY ?? ne.pageY };
  }, []);

  // Window pointer listeners are attached only for the lifetime of a drag (web).
  const detachRef = useRef<(() => void) | null>(null);
  useEffect(() => () => detachRef.current?.(), []); // clean up if unmounted mid-drag

  const handleLongPress = useCallback(
    (e: GestureResponderEvent) => {
      if (!draggable || !drag) return;
      // Drag is web-only (window pointer events). Guard so a native/SSR render
      // with draggable enabled can't throw on undefined `window`.
      if (typeof window === 'undefined') return;
      // Detach any listeners from a prior drag before attaching new ones, so a
      // second long-press without an intervening pointerup can't leak listeners.
      detachRef.current?.();
      recordPoint(e);
      draggedRef.current = true;
      const { x, y } = lastPointRef.current;
      drag.startDrag(event.id, event.at, x, y);

      // Drive the rest of the gesture from window pointer events — RN's responder
      // doesn't track moves across sibling cells. Client coords (viewport) match
      // the getBoundingClientRect hit-test. Web-only by design.
      const onMove = (ev: PointerEvent) => drag.updateDrag(ev.clientX, ev.clientY);
      const onUp = (ev: PointerEvent) => {
        drag.endDrag(ev.clientX, ev.clientY);
        detach();
      };
      const onCancel = () => {
        drag.cancelDrag();
        detach();
      };
      const detach = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        detachRef.current = null;
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      detachRef.current = detach;
    },
    [draggable, drag, event.id, event.at, recordPoint],
  );

  const handlePress = useCallback(
    (e: GestureResponderEvent) => {
      e.stopPropagation();
      // A completed drag must not also open the activity.
      if (draggedRef.current) {
        draggedRef.current = false;
        return;
      }
      onPress?.(event.id);
    },
    [onPress, event.id],
  );

  return (
    <Pressable
      onPress={handlePress}
      onPressIn={draggable ? recordPoint : undefined}
      onLongPress={draggable ? handleLongPress : undefined}
      delayLongPress={250}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        calendarStyles.pill,
        dense && calendarStyles.pillDense,
        { backgroundColor: event.color },
        selected && calendarStyles.pillSelected,
        hovered && !isDragging && { opacity: 0.9 },
        isDragging && calendarStyles.pillDragging,
        // Cursor: `grab` when drag is enabled; otherwise `pointer` when the pill
        // is clickable (opens the activity). Drag is currently disabled at the
        // consumer (see activities.tsx), so pills read as plain clickable items.
        (draggable
          ? { cursor: 'grab' }
          : onPress
          ? { cursor: 'pointer' }
          : undefined) as object,
      ]}
      accessibilityRole="button"
      accessibilityLabel={event.asset ? `${event.title}, ${event.asset}` : event.title}
      accessibilityState={{ selected }}
      testID={testID}
    >
      <Text style={calendarStyles.pillTitle} numberOfLines={1} ellipsizeMode="tail">
        {event.title}
      </Text>
      {showAsset && event.asset ? (
        <Text style={calendarStyles.pillAsset} numberOfLines={1} ellipsizeMode="tail">
          {event.asset}
        </Text>
      ) : null}
    </Pressable>
  );
};

EventPill.displayName = 'EventPill';
